import { useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import LogoContainer from "./LogoContainer";
import { sign_up_user } from "../../../DAL/auth/auth";
import { useAdminContext } from "../../../hooks/UseAdminContext";
import PhoneInput from "react-phone-number-validation";
import { isValidPhoneNumber } from "libphonenumber-js";
import DashboardHeader from "../../headers/DashboardHeader";

export default function SignupForm() {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordVisibleConfirm, setPasswordVisibleConfirm] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setUserInfo } = useAdminContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordRules, setShowPasswordRules] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneData, setPhoneData] = useState({});

  const handleChangePhone = (value, country) => {
    setPhoneData(country);
    setPhoneNumber(value);
  };

  const [inputs, setInputs] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    contact_number: "",
  });

  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const getPasswordValidation = (password) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      specialChar:
        /[\!\@\#\$\%\^\&\*\(\)\_\+\-\=\{\}\[\]\:\;\"\'\<\>\,\.\/\\\|\`\~\?]/.test(
          password
        ),
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      setPasswordValidation(getPasswordValidation(value));
    }
  };

  const getUserTimeZoneDisplay = () => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const offsetMinutes = now.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes <= 0 ? "+" : "-";

    const formattedOffset = `GMT${sign}${offsetHours
      .toString()
      .padStart(2, "0")}:${offsetMins.toString().padStart(2, "0")}`;
    return `${timeZone} (${formattedOffset})`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValidNumberCheck = isValidPhoneNumber(
      phoneNumber,
      phoneData?.iso2?.toUpperCase()
    );

    const passwordRegex =
      /[\!\@\#\$\%\^\&\*\(\)\_\+\-\=\{\}\[\]\:\;\"\'\<\>\,\.\/\\\|\`\~\?]/.test(
        inputs.password
      );

    if (!passwordRegex) {
      enqueueSnackbar("Password does not meet requirements.", {
        variant: "error",
      });
      return;
    }

    if (inputs.password !== inputs.confirm_password) {
      enqueueSnackbar(
        "Oops! Your passwords don’t match. Please make sure both fields are the same.",
        {
          variant: "error",
        }
      );
      return;
    }

    if (!isValidNumberCheck) {
      enqueueSnackbar("Please enter a valid phone number", {
        variant: "error",
      });
      return;
    }

    const sign_up_data = {
      name: inputs.name,
      email: inputs.email,
      password: inputs.password,
      confirm_password: inputs.confirm_password,
      contact_number: phoneNumber,
      time_zone: getUserTimeZoneDisplay(),
    };

    setIsLoading(true);
    const response = await sign_up_user(sign_up_data);
    if (response.code === 200) {
      localStorage.setItem("user", JSON.stringify(response.customer));
      localStorage.setItem("token", response.customer.token);
      setUserInfo(response.customer);
      navigate("/two-step-verification");
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
    setIsLoading(false);
  };
  return (
    <div className="header-container">
      <DashboardHeader />
      <div className="create-account-page-layout">
        <div className="create-account-page-logo-panel">
          <LogoContainer />
        </div>
        <div className="create-account-page-form-scroll-panel">
          <div className="create-account-page-form-inner-content">
            <h1 className="sign-in-title">Sign Up</h1>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                required
                label="Name"
                name="name"
                value={inputs.name}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={inputs.email}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                required
                label="Password"
                name="password"
                type={passwordVisible ? "text" : "password"}
                value={inputs.password}
                onChange={handleChange}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        edge="end"
                        aria-label="toggle password visibility"
                      >
                        {passwordVisible ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <div
                style={{
                  textAlign: "right",
                  marginBottom: "8px",
                  cursor: "pointer",
                  color: "var(--portal-red-colr)",
                  fontSize: "14px",
                  userSelect: "none",
                }}
                onClick={() => setShowPasswordRules(!showPasswordRules)}
              >
                Password Requirements
              </div>
              {showPasswordRules && (
                <div
                  style={{
                    backgroundColor: "#f0f0f0",
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 14,
                    color: "#555",
                    marginBottom: 12,
                    lineHeight: 1.5,
                  }}
                >
                  <p
                    className="fs-14"
                    style={{
                      color: passwordValidation.length ? "green" : "#c10000",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {passwordValidation.length ? (
                      <CheckCircleIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    )}
                    At least 8 characters
                  </p>
                  <p
                    className="fs-14"
                    style={{
                      color: passwordValidation.uppercase ? "green" : "#c10000",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {passwordValidation.uppercase ? (
                      <CheckCircleIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    )}
                    At least one uppercase letter
                  </p>
                  <p
                    className="fs-14"
                    style={{
                      color: passwordValidation.number ? "green" : "#c10000",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {passwordValidation.number ? (
                      <CheckCircleIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    )}
                    At least one number
                  </p>
                  <p
                    className="fs-14 mb-0"
                    style={{
                      color: passwordValidation.specialChar
                        ? "green"
                        : "#c10000",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {passwordValidation.specialChar ? (
                      <CheckCircleIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                    )}
                    At least one special character (@, #, $, etc.)
                  </p>
                </div>
              )}

              <TextField
                fullWidth
                required
                label="Confirm Password"
                name="confirm_password"
                type={passwordVisibleConfirm ? "text" : "password"}
                value={inputs.confirm_password}
                onChange={handleChange}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setPasswordVisibleConfirm(!passwordVisibleConfirm)
                        }
                        edge="end"
                        aria-label="toggle confirm password visibility"
                      >
                        {passwordVisibleConfirm ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className="mt-3 position-relative">
                <div className="small_title">Phone Number *</div>
                <PhoneInput
                  placeholder=" "
                  value={phoneNumber}
                  setValue={setPhoneNumber}
                  onChange={handleChangePhone}
                  inputClass="phone_input"
                  autoSelectCountry={true}
                  enableSearch={true}
                  countryCodeEditable={false}
                  required={true}
                />
              </div>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isLoading}
                sx={{ mt: 2, mb: 2 }}
              >
                {isLoading ? "Submitting..." : "Register"}
              </Button>
            </form>

            <div className="text-end">
              <Button className="w-100" onClick={() => navigate("/login")}>
                Already have an account? Sign In
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
